import { Dispatch, memo, SetStateAction, useMemo, useState } from 'react';

import useElementSize from '@custom-react-hooks/use-element-size';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import Kpi from 'types/Kpi';

import { DirtyCell, Month } from '../../types';
import KpiRow from './components/KpiRow/KpiRow';
import KpiTableHeader from './components/KpiTableHeader/KpiTableHeader';
import useCellId from './hooks/useCellId';
import { StyledTableContainer } from './styled';
import { compareKpis } from './utils';

export interface Props {
    companyId: number;
    assessmentId: number;
    kpis: Kpi[];
    months: Month[];
    canAddNewMonth: boolean;
    addMonth: () => void;
    getDataForMonth: (index: number, month: number, year: number) => number | '';
    setDataForMonth: (index: number, month: number, year: number, amount: number | '') => void;
    getKpiNote: (index: number) => string;
    setKpiNote: (index: number, note: string) => void;
    setDirtyCell: Dispatch<SetStateAction<DirtyCell | null>>;
    editDisabled: boolean;
}

const KpiTable = (props: Props) => {
    const { kpis, months, canAddNewMonth, addMonth, ...otherProps } = props;

    const [shouldFocusCellId, setShouldFocusCellId] = useState<number | null>(null);
    const [minimumApproveButtonWidth, setMinimumApproveButtonWidth] = useState(0);
    const [setRefFirstColumn, sizeFirstColumn] = useElementSize();

    const { getCellId } = useCellId();

    const sortedKpis = useMemo(() => {
        return kpis.sort(compareKpis);
    }, [kpis]);

    const firstColumnStyle = useMemo((): React.CSSProperties => {
        return {
            position: 'sticky',
            left: 0,
            background: 'white',
            boxShadow: '2px 0px 0px 0px #999999',
            zIndex: 10
        };
    }, []);

    const secondColumnStyle = useMemo((): React.CSSProperties => {
        return {
            position: 'sticky',
            left: sizeFirstColumn.width + 'px',
            background: 'white',
            boxShadow: '2px 0px 0px 0px #999999',
            zIndex: 10
        };
    }, [sizeFirstColumn.width]);

    return (
        <StyledTableContainer>
            <Table style={{ borderCollapse: 'separate' }}>
                <KpiTableHeader setRefFirstColumn={setRefFirstColumn} firstColumnStyle={firstColumnStyle} secondColumnStyle={secondColumnStyle} months={months} canAddNewMonth={canAddNewMonth} addMonth={addMonth} />
                <TableBody>
                    {sortedKpis.map((kpi, index) => {
                        return (
                            <KpiRow
                                kpi={kpi}
                                index={index}
                                getCellId={getCellId}
                                months={months}
                                minimumApproveButtonWidth={minimumApproveButtonWidth}
                                setMinimumApproveButtonWidth={setMinimumApproveButtonWidth}
                                shouldFocusCellId={shouldFocusCellId}
                                setShouldFocusCellId={setShouldFocusCellId}
                                firstColumnStyle={firstColumnStyle}
                                secondColumnStyle={secondColumnStyle}
                                {...otherProps}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        </StyledTableContainer>
    );
};

export default memo(KpiTable);